import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useActions } from '../../../hooks/useActions';
import { useProcessing } from '../../../hooks/useProcessing';
import { ProductType } from '../../../models/ProductType';
import { ProductSubscriptionRenewPageParamsType } from '../../../models/types/RoutePath';
import { clearOrganizationContracts } from '../../../store/redux-slices/contracts';
import { organizationSelector } from '../../../store/redux-slices/organizations/selectors';
import {
  ORGANIZATION_LOADING_PROCESSING,
  SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  SUBSCRIPTION_EDITING_LOADING_PROCESSING,
  KE_PRICING_LOADING_PROCESSING,
} from '../../../store/redux-slices/processes/constants';
import { clearSubscriptionHelperData } from '../../../store/redux-slices/subscriptions/common';
import {
  clearKECurrentSubscriptionEditing,
  clearKESubscriptionHelperData,
} from '../../../store/redux-slices/subscriptions/kidnapAndExtortion';
import {
  editingKESubscriptionDataSelector,
  keSubscriptionRenewalSuggestionSelector,
} from '../../../store/redux-slices/subscriptions/kidnapAndExtortion/selectors';
import { loadOrganizationContracts } from '../../../store/saga-slices/contracts';
import { loadOrganization } from '../../../store/saga-slices/organizations';
import { keLoadStandardPricing } from '../../../store/saga-slices/pricing/kidnapAndExtortion';
import { ILoadDatesForNewSubscriptionPayload } from '../../../store/saga-slices/subscriptions/common/models';
import {
  loadDatesForNewKESubscription,
  loadEditingKESubscription,
  loadKESubscriptionRenewalSuggestion,
} from '../../../store/saga-slices/subscriptions/kidnapAndExtortion';
import {
  ILoadEditingKESubscriptionPayload,
  ILoadKESubscriptionRenewalSuggestionPayload,
} from '../../../store/saga-slices/subscriptions/kidnapAndExtortion/models';

export const useKEEditSubscriptionRenewPage = () => {
  const { organizationId, subscriptionId, productType } =
    useParams<ProductSubscriptionRenewPageParamsType>();

  const LoadOrganization = useActions(loadOrganization);
  const LoadOrganizationContracts = useActions(loadOrganizationContracts);
  const LoadDatesForNewSubscription = useActions(loadDatesForNewKESubscription);
  const ClearOrganizationContracts = useActions(clearOrganizationContracts);
  const LoadEditingSubscription = useActions(loadEditingKESubscription);
  const LoadStandardPricing = useActions(keLoadStandardPricing);
  const ClearKESubscriptionHelperData = useActions(
    clearKESubscriptionHelperData,
  );
  const subscriptionData = useSelector(editingKESubscriptionDataSelector);

  const ClearCurrentSubscriptionEditing = useActions(
    clearKECurrentSubscriptionEditing,
  );
  const ClearSubscriptionHelperData = useActions(clearSubscriptionHelperData);
  const LoadSubscriptionRenewalSuggestion = useActions(
    loadKESubscriptionRenewalSuggestion,
  );

  const subscriptionRenewalSuggestion = useSelector(
    keSubscriptionRenewalSuggestionSelector,
  );

  const isOrganizationDataLoading = useProcessing(
    ORGANIZATION_LOADING_PROCESSING,
  );

  const isEditingDataLoading = useProcessing(
    SUBSCRIPTION_EDITING_LOADING_PROCESSING,
  );

  const isSuggestionDataLoading = useProcessing(
    SUBSCRIPTION_RENEWAL_SUGGESTION_LOADING_PROCESSING,
  );

  const isPricingDataLoading = useProcessing(KE_PRICING_LOADING_PROCESSING);
  const organization = useSelector(organizationSelector);

  const loadSubscriptionRenewalSuggestionHandler = useCallback(() => {
    const payload: ILoadKESubscriptionRenewalSuggestionPayload = {
      organizationId,
      subscriptionId,
    };
    LoadSubscriptionRenewalSuggestion(payload);
  }, [LoadSubscriptionRenewalSuggestion, organizationId, subscriptionId]);

  const loadDatesForNewSubscriptionHandler = useCallback(
    (value: string, forceLoad = false) => {
      if (!value && !forceLoad) {
        return;
      }

      const loadDatesForNewSubscriptionPayload: ILoadDatesForNewSubscriptionPayload =
        {
          organizationId,
          contractId: value,
        };
      LoadDatesForNewSubscription(loadDatesForNewSubscriptionPayload);
    },
    [LoadDatesForNewSubscription, organizationId],
  );

  useEffect(() => {
    const payload: ILoadEditingKESubscriptionPayload = {
      organizationId,
      subscriptionId,
      subscriptionType: productType,
    };

    LoadEditingSubscription(payload);
  }, [LoadEditingSubscription, organizationId, productType, subscriptionId]);

  useEffect(() => {
    LoadOrganizationContracts({ organizationId });
    LoadOrganization(organizationId);
    loadSubscriptionRenewalSuggestionHandler();

    LoadStandardPricing({ productType: ProductType.KidnapAndExtortion });
  }, [
    LoadOrganization,
    LoadOrganizationContracts,
    organizationId,
    subscriptionId,
    LoadStandardPricing,
    loadSubscriptionRenewalSuggestionHandler,
  ]);

  useEffect(
    () => () => {
      ClearKESubscriptionHelperData();
      ClearOrganizationContracts();
      ClearSubscriptionHelperData();
      ClearCurrentSubscriptionEditing();
    },
    [
      ClearOrganizationContracts,
      ClearKESubscriptionHelperData,
      ClearSubscriptionHelperData,
      ClearCurrentSubscriptionEditing,
    ],
  );

  const dataLoading =
    !organization ||
    isOrganizationDataLoading ||
    !subscriptionData ||
    (!subscriptionData && isEditingDataLoading) ||
    !subscriptionRenewalSuggestion ||
    (!subscriptionRenewalSuggestion && isSuggestionDataLoading) ||
    isPricingDataLoading;

  return {
    dataLoading,
    organization,
    loadDatesForNewSubscriptionHandler,
    loadSubscriptionRenewalSuggestionHandler,
  };
};
